import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  createMotivationCode,
  deleteMotivationCode,
  listData,
  updateMotivationCode,
} from "../services/api";

const EMPTY_FORM = {
  motivationCode: "",
  description: "",
  status: "Ativo",
  productCode: "",
  campaignNumber: "",
  url: "",
  channelId: "",
};

export default function MotivationCodes() {
  const navigate = useNavigate();

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const [filterCode, setFilterCode] = useState("");
  const [filterChannelId, setFilterChannelId] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [form, setForm] = useState(EMPTY_FORM);

  function applyFilters(codes, code, channelId) {
    let filtered = codes;
    if (code.trim()) {
      const lower = code.trim().toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.motivation_code || "").toLowerCase().includes(lower) ||
          (r.description || "").toLowerCase().includes(lower)
      );
    }
    if (channelId) {
      filtered = filtered.filter((r) => r.channel_id === channelId);
    }
    return filtered;
  }

  async function loadAll() {
    setIsLoading(true);
    try {
      const [codesRes, channelsRes] = await Promise.all([
        listData({
          table: "motivation-codes",
          term: "",
          value: "",
          limit: 0,
          cursor: "",
        }),
        listData({
          table: "channels",
          term: "",
          value: "",
          limit: 0,
          cursor: "",
        }),
      ]);

      if (codesRes instanceof Error) throw codesRes;
      if (channelsRes instanceof Error) throw channelsRes;

      const codes = (codesRes.data || []).filter((item) => !item.count);
      setAllRows(codes);
      setRows(applyFilters(codes, filterCode, filterChannelId));
      setChannels(channelsRes.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load motivation codes");
    } finally {
      setIsLoading(false);
      setIsFiltering(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFilter() {
    setIsFiltering(true);
    setRows(applyFilters(allRows, filterCode, filterChannelId));
    setIsFiltering(false);
  }

  function handleClearFilters() {
    setFilterCode("");
    setFilterChannelId("");
    setRows(allRows);
  }

  function handleFilterKeyDown(e) {
    if (e.key === "Enter") {
      handleFilter();
    }
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setModalMode("create");
    setModalOpen(true);
  }

  function openEdit(row) {
    setForm({
      motivationCode: row.motivation_code || "",
      description: row.description || "",
      status: row.status || "Ativo",
      productCode: row.product_code || "",
      campaignNumber: row.campaign_number || "",
      url: row.url || "",
      channelId: row.channel_id || "",
    });
    setModalMode("edit");
    setModalOpen(true);
  }

  function handleField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!form.motivationCode.trim()) {
      toast.error("Motivation Code is required");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    if (!form.channelId) {
      toast.error("Channel is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        motivationCode: form.motivationCode.trim(),
        description: form.description,
        status: form.status || "Ativo",
        productCode: form.productCode || undefined,
        campaignNumber: form.campaignNumber || undefined,
        url: form.url || undefined,
        channelId: form.channelId,
      };

      const response =
        modalMode === "create"
          ? await createMotivationCode(payload)
          : await updateMotivationCode(payload);
      if (response instanceof Error) throw response;

      toast.success(
        modalMode === "create"
          ? "Motivation code created"
          : "Motivation code updated"
      );
      setModalOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Failed to save motivation code");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    const ok = window.confirm(
      `Delete motivation code "${row.motivation_code}"?`
    );
    if (!ok) return;
    setSaving(true);
    try {
      const response = await deleteMotivationCode(row.motivation_code);
      if (response instanceof Error) throw response;
      toast.success("Motivation code deleted");
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Failed to delete motivation code");
    } finally {
      setSaving(false);
    }
  }

  function statusBadge(value) {
    const v = (value || "").toLowerCase();
    const style =
      v === "ativo"
        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
        : v === "inativo"
        ? "bg-slate-100 text-slate-600 border-slate-200"
        : "bg-slate-100 text-slate-700 border-slate-200";
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${style}`}
      >
        {value || "—"}
      </span>
    );
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <span className="text-slate-500 text-xs uppercase tracking-wider">
            Ferramentas
          </span>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            Motivation Codes
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={openCreate}>
            + New
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[280px] flex-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Buscar
            </label>
            <Form.Control
              type="text"
              placeholder="Filtrar por código ou descrição"
              value={filterCode}
              onChange={(e) => setFilterCode(e.target.value)}
              onKeyDown={handleFilterKeyDown}
              disabled={isFiltering}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Channel
            </label>
            <Form.Select
              value={filterChannelId}
              onChange={(e) => setFilterChannelId(e.target.value)}
              disabled={isFiltering}
            >
              <option value="">Todos</option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="primary" onClick={handleFilter} disabled={isFiltering}>
              {isFiltering ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Buscando...
                </>
              ) : (
                "Filtrar"
              )}
            </Button>
            {(filterCode || filterChannelId) && (
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                disabled={isFiltering}
              >
                Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-700">
            {rows.length} {rows.length === 1 ? "registro" : "registros"}
          </span>
        </div>
        {isLoading ? (
          <div className="w-full text-center py-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhum motivation code encontrado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Channel
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.motivation_code}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-900">
                      {row.motivation_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-[280px]">
                      {row.description || (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {statusBadge(row.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {row.channel?.name || row.channel_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                      {row.product_code || (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                      {row.campaign_number || (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.url ? (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Abrir ↗
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            navigate(
                              `${encodeURIComponent(row.motivation_code)}/content`
                            )
                          }
                        >
                          Content
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        backdrop="static"
      >
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalMode === "create"
                ? "New Motivation Code"
                : "Edit Motivation Code"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning" className="mb-3">
              <Alert.Heading className="h6 mb-1">Atenção: Cadastro no Salesforce</Alert.Heading>
              Este Motivation Code também precisa ser cadastrado no Salesforce CRM
              para que a integração de subscriptions e doações funcione corretamente.
              Caso contrário, o Salesforce irá rejeitar o registro.
            </Alert>
            <Form.Group className="py-2">
              <Form.Label>Motivation Code *</Form.Label>
              <Form.Control
                type="text"
                value={form.motivationCode}
                readOnly={modalMode === "edit"}
                onChange={(e) => handleField("motivationCode", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => handleField("description", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>Channel *</Form.Label>
              <Form.Select
                value={form.channelId}
                onChange={(e) => handleField("channelId", e.target.value)}
              >
                <option value="">-- select channel --</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => handleField("status", e.target.value)}
              >
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>Product Code</Form.Label>
              <Form.Control
                type="text"
                value={form.productCode}
                onChange={(e) => handleField("productCode", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>Campaign Number</Form.Label>
              <Form.Control
                type="text"
                value={form.campaignNumber}
                onChange={(e) => handleField("campaignNumber", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="py-2">
              <Form.Label>URL</Form.Label>
              <Form.Control
                type="text"
                value={form.url}
                onChange={(e) => handleField("url", e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
